// Save · Teach · Email · Print · Share for an adaptation. Handles guest → sign-in → claim.
import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { Link } from "../lib/router";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import type { Adaptation } from "../lib/types";
import { Icon } from "./Icon";
import { SignInPanel } from "./SignInPanel";

export function AdaptationActions({ a, onChange, readOnly = false }: { a: Adaptation; onChange: (a: Adaptation) => void; readOnly?: boolean }) {
  const { me, refresh } = useSession();
  const toast = useToast();
  const [pending, setPending] = useState<"" | "save" | "email" | "share">("");
  const [signInFor, setSignInFor] = useState<"" | "save" | "email" | "share">("");

  const run = async (what: "save" | "email" | "share") => {
    if (!me?.user) return setSignInFor(what);
    setPending(what);
    try {
      if (what === "save") {
        const r = await api.updateAdaptation(a.id, { saved: !a.saved, title: a.title ?? a.output.title });
        onChange(r.adaptation);
        toast(r.adaptation.saved ? "Saved to your shelf" : "Removed from your shelf");
      } else if (what === "email") {
        const r = await api.emailLesson({ adaptationId: a.id });
        toast(`Sent to ${r.to}`);
      } else {
        const r = a.shareSlug ? a : (await api.updateAdaptation(a.id, { share: true })).adaptation;
        onChange(r);
        const url = `${location.origin}/s/${r.shareSlug}`;
        await navigator.clipboard.writeText(url).catch(() => undefined);
        toast("Share link copied. Anyone with the link can view it.");
      }
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setPending("");
    }
  };

  const unshare = async () => {
    const r = await api.updateAdaptation(a.id, { share: false });
    onChange(r.adaptation);
    toast("Link switched off");
  };

  return (
    <div className="actions">
      <div className="actions-row">
        {!readOnly && (
          <button className={`btn${a.saved ? " is-on" : " btn-primary"}`} onClick={() => void run("save")} disabled={pending === "save"} aria-pressed={!!a.saved}>
            <Icon name={a.saved ? "check" : "save"} /> {a.saved ? "Saved" : "Save"}
          </button>
        )}
        <Link className="btn" to={readOnly && a.shareSlug ? `/teach/s/${a.shareSlug}` : `/teach/a/${a.id}`}><Icon name="play" /> Teach</Link>
        {!readOnly && <button className="btn btn-quiet" onClick={() => void run("email")} disabled={pending === "email"}><Icon name="mail" /> Email me</button>}
        <button className="btn btn-quiet" onClick={() => window.print()}><Icon name="print" /> Print</button>
        {!readOnly && <button className="btn btn-quiet" onClick={() => void run("share")} disabled={pending === "share"}><Icon name="share" /> {a.shareSlug ? "Copy link" : "Share"}</button>}
      </div>
      {a.shareSlug && !readOnly && (
        <p className="muted small">
          Shared at <a href={`/s/${a.shareSlug}`}>{location.host}/s/{a.shareSlug}</a>. <button className="btn-link" onClick={() => void unshare()}>Stop sharing</button>
        </p>
      )}
      {signInFor && (
        <SignInPanel
          reason={{ save: "Sign in to keep this adaptation.", email: "Sign in and we'll email it to you.", share: "Sign in to create a share link." }[signInFor]}
          onDone={async () => {
            const what = signInFor;
            setSignInFor("");
            await refresh();
            // The guest result is claimed by the first signed-in action on it.
            const r = await api.updateAdaptation(a.id, what === "save" ? { saved: true, title: a.output.title } : {}).catch(() => null);
            if (r) onChange(r.adaptation);
            toast(what === "save" ? "Signed in and saved" : "Signed in. Tap it again to continue.");
          }}
        />
      )}
    </div>
  );
}
